import React, { useState, useRef, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import CandidateSidebar from '../components/candidate/CandidateSidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import CandidateDashboardHeaderForProfile from '../components/candidate/CandidateDashboardHeaderForProfile';
import CandidateMobileHeader from '../components/candidate/CandidateMobileHeader';
import CandidateMobileMenu from '../components/candidate/CandidateMobileMenu';
import EducationSelector from '../components/common/EducationSelector';
import { useUser } from '../context/UserContext';
import { AuthContext } from '../context/AuthContext';
import { Upload, Camera, AlertCircle, Check, Briefcase, GraduationCap, FileText } from 'lucide-react';
import GradientButton from '../components/ui/GradientButton';
import useResponsive from '../hooks/useResponsive';

const ProfileSettingsPage = () => {
  const { user: authUser } = useContext(AuthContext);
  const { user, updateUser, refreshUserData } = useUser();
  const { isMobile } = useResponsive();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Determine user type from AuthContext
  const userType = authUser?.userType || 'candidate';
  const isCandidate = userType === 'candidate';
  
  // Initialize form data based on user type
  const [formData, setFormData] = useState({
    // Common fields
    name: user?.name || '',
    contactNumber: user?.contactNumber || '',
    email: user?.email || user?.businessEmail || '',
    avatar: user?.avatar || null,
    
    // Employer-specific fields
    companyName: user?.companyName || '',
    businessEmail: user?.businessEmail || '',
    companySize: user?.companySize || '',
    industry: user?.industry || '',
    
    // Candidate-specific fields
    title: user?.title || '',
    skills: Array.isArray(user?.skills) ? user?.skills : (user?.skills ? [user.skills] : []),
    experience: user?.experience || '',
    education: Array.isArray(user?.education) ? user?.education : (user?.education ? [user.education] : []),
    resumeUrl: user?.resumeUrl || '',
    location: user?.location || '',
    remotePreference: user?.remotePreference || 'no-preference'});
  
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [imageError, setImageError] = useState('');
  const [previewImage, setPreviewImage] = useState(user?.avatar || null);
  const [skill, setSkill] = useState('');
  const fileInputRef = useRef(null);
  const resumeFileRef = useRef(null);

  // Fetch fresh user data on component mount
  useEffect(() => {
    refreshUserData();
  }, [refreshUserData]);

  // Sync formData when user data updates (without reload)
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        contactNumber: user.contactNumber || prev.contactNumber,
        email: user.email || user.businessEmail || prev.email,
        avatar: user.avatar || prev.avatar,
        // Employer fields
        companyName: user.companyName || prev.companyName,
        businessEmail: user.businessEmail || prev.businessEmail,
        companySize: user.companySize || prev.companySize,
        industry: user.industry || prev.industry,
        // Candidate fields
        title: user.title || prev.title,
        skills: Array.isArray(user.skills) ? user.skills : (user.skills ? [user.skills] : prev.skills),
        experience: user.experience !== undefined ? user.experience : prev.experience,
        education: Array.isArray(user.education) ? user.education : (user.education ? [user.education] : prev.education),
        resumeUrl: user.resumeUrl || prev.resumeUrl,
        location: user.location || prev.location,
        remotePreference: user.remotePreference || prev.remotePreference}));
      if (user.avatar) {
        setPreviewImage(user.avatar);
      }
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const validateImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;
        img.onload = () => {
          if (img.width !== 512 || img.height !== 512) {
            reject('Image must be exactly 512x512 pixels');
          } else {
            resolve(e.target.result);
          }
        };
        img.onerror = () => reject('Invalid image file');
      };
      reader.onerror = () => reject('Error reading file');
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset previous errors
    setImageError('');
    
    // Check file type
    if (!file.type.match('image.*')) {
      setImageError('Please select an image file');
      return;
    }
    
    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setImageError('Image size should be less than 2MB');
      return;
    }
    
    try {
      // Validate dimensions
      const dataUrl = await validateImage(file);
      setPreviewImage(dataUrl);
      setFormData(prev => ({ ...prev, avatar: dataUrl }));
    } catch (err) {
      setImageError(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    
    try {
      // Log what we're about to save

      const result = await updateUser(formData);
      if (result) {
        setSuccess(true);

        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      } else {
        setError('Failed to update profile. Please try again.');
        console.error('[ERROR] Profile update failed');
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred.');
      console.error('[ERROR] Profile update error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Add a skill to the skills array
  const addSkill = () => {
    // Ensure skills is an array
    const currentSkills = Array.isArray(formData.skills) ? formData.skills : [];
    if (skill && !currentSkills.includes(skill)) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, skill]
      }));
      setSkill('');
    }
  };

  // Remove a skill from the skills array
  const removeSkill = (skillToRemove) => {
    setFormData(prev => ({
      ...prev,
      skills: Array.isArray(prev.skills) ? prev.skills.filter(s => s !== skillToRemove) : []
    }));
  };

  // Handle resume file upload
  const handleResumeUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // In a real app, you would upload this file to a server
    // For now, we'll just store the file name
    setFormData(prev => ({
      ...prev,
      resumeUrl: file.name
    }));
  };

  return (
    <div className="flex bg-gray-50 min-h-screen font-sans relative">
      {isCandidate ? <CandidateSidebar /> : <Sidebar />}
      
      {/* Mobile Menu - simplified for mobile */}
      {isCandidate && isMobile && mobileMenuOpen && <CandidateMobileMenu setMobileMenuOpen={setMobileMenuOpen} />}
      
      <main className="flex-1 p-4 md:p-8 ml-0 md:ml-24">
        {isCandidate ? (
          isMobile ? (
            <CandidateMobileHeader mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
          ) : (
            <CandidateDashboardHeaderForProfile user={user} />
          )
        ) : (
          <DashboardHeader />
        )}
        <div className="mt-8 bg-white p-8 rounded-lg border border-gray-200 transform transition-all" 
          style={{ 
            boxShadow: '3px 3px 0 rgba(0, 0, 0, 0.8)',
            transform: 'translateX(-3px) translateY(-3px)'
          }}>
          <h1 className="text-2xl font-bold text-black mb-6 border-b border-black pb-3">
            {isCandidate ? 'Candidate Profile Settings' : 'Employer Profile Settings'}
          </h1>
          
          {success && (
            <div className="bg-green-50 text-green-800 p-4 rounded-lg shadow-md mb-6 transform transition-all">
              <div className="flex items-center">
                <Check className="h-5 w-5 mr-2" />
                <div>
                  <p className="font-medium">Profile updated successfully!</p>
                  <p className="text-sm text-green-700 mt-1">Your changes have been saved.</p>
                </div>
              </div>
            </div>
          )}
          
          {error && (
            <div className="bg-red-50 text-red-800 p-4 rounded-lg shadow-md mb-6 transform transition-all">
              <div className="flex items-center">
                <AlertCircle className="mr-2" size={18} />
                <span>{error}</span>
              </div>
            </div>
          )}
          
          {/* Tabs for different sections */}
          <div className="flex border-b border-black mb-6">
            <button
              className={`px-4 py-2 font-medium ${activeTab === 'profile' ? 'border-b-2 border-black' : 'text-gray-500'}`}
              onClick={() => setActiveTab('profile')}
            >
              Basic Info
            </button>
            {isCandidate ? (
              <>
                <button
                  className={`px-4 py-2 font-medium ${activeTab === 'skills' ? 'border-b-2 border-black' : 'text-gray-500'}`}
                  onClick={() => setActiveTab('skills')}
                >
                  Skills & Experience
                </button>
                <button
                  className={`px-4 py-2 font-medium ${activeTab === 'resume' ? 'border-b-2 border-black' : 'text-gray-500'}`}
                  onClick={() => setActiveTab('resume')}
                >
                  Resume
                </button>
              </>
            ) : (
              <>
                <button
                  className={`px-4 py-2 font-medium ${activeTab === 'company' ? 'border-b-2 border-black' : 'text-gray-500'}`}
                  onClick={() => setActiveTab('company')}
                >
                  Company Details
                </button>
              </>
            )}
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Profile Picture Upload - Common for both user types */}
            {activeTab === 'profile' && (
              <div className="flex flex-col items-center mb-8 pt-4">
                <label className="block text-sm font-medium text-black mb-3">Profile Picture</label>
                <div className="relative group cursor-pointer" onClick={handleImageClick}>
                  <div className="w-32 h-32 overflow-hidden rounded-lg shadow-lg bg-white flex items-center justify-center group-hover:bg-gray-50 transition-all transform group-hover:-translate-y-1"
                    style={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
                    {previewImage ? (
                      <img 
                        src={previewImage} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Camera size={40} className="text-gray-400" />
                    )}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center transition-all">
                    <div className="bg-black text-white p-2 opacity-0 group-hover:opacity-100 transition-all">
                      <Upload size={16} />
                    </div>
                  </div>
                  <input 
                    type="file" 
                    ref={fileInputRef} 
                    onChange={handleImageChange} 
                    accept="image/*" 
                    className="hidden" 
                  />
                </div>
                <div className="mt-3 text-center">
                  <p className="text-xs text-black">Upload a 512x512 pixel image (max 2MB)</p>
                  {imageError && (
                    <div className="flex items-center gap-1 text-black text-xs mt-1">
                      <AlertCircle size={12} />
                      <span>{imageError}</span>
                    </div>
                  )}
                  {previewImage && !imageError && (
                    <div className="flex items-center gap-1 text-black text-xs mt-1">
                      <Check size={12} />
                      <span>Image ready to upload</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Basic Info Tab - Common fields with conditional rendering */}
            {activeTab === 'profile' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-black mb-2">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors"
                    required
                  />
                </div>
                
                {isCandidate ? (
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-black mb-2">Professional Title</label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors"
                      placeholder="e.g. Senior Software Engineer"
                    />
                  </div>
                ) : (
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-black mb-2">Company Name</label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors"
                      required
                    />
                  </div>
                )}
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-black mb-2">
                    {isCandidate ? 'Email Address' : 'Business Email'}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="contactNumber" className="block text-sm font-medium text-black mb-2">Contact Number</label>
                  <input
                    type="tel"
                    id="contactNumber"
                    name="contactNumber"
                    value={formData.contactNumber}
                    onChange={handleChange}
                    className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors"
                  />
                </div>
                
                {isCandidate && (
                  <div>
                    <label htmlFor="location" className="block text-sm font-medium text-black mb-2">Location</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors"
                      placeholder="e.g. Mumbai, Bangalore, Remote"
                    />
                  </div>
                )}
                
                {isCandidate && (
                  <div>
                    <label htmlFor="remotePreference" className="block text-sm font-medium text-black mb-2">Remote Work Preference</label>
                    <select
                      id="remotePreference"
                      name="remotePreference"
                      value={formData.remotePreference}
                      onChange={handleChange}
                      className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors"
                    >
                      <option value="no-preference">No Preference</option>
                      <option value="remote-only">Remote Only</option>
                      <option value="hybrid">Hybrid</option>
                      <option value="on-site">On-site Only</option>
                    </select>
                  </div>
                )}
              </div>
            )}
            
            {/* Skills & Experience Tab - Candidate only */}
            {isCandidate && activeTab === 'skills' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-black mb-2">Skills</label>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={skill}
                      onChange={(e) => setSkill(e.target.value)}
                      className="flex-1 px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors"
                      placeholder="Add a skill (e.g. JavaScript)"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    />
                    <button
                      type="button"
                      onClick={addSkill}
                      className="px-4 py-2 bg-black text-white"
                    >
                      Add
                    </button>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mt-3">
                    {Array.isArray(formData.skills) && formData.skills.map((s, index) => (
                      <div key={index} className="flex items-center bg-gray-100 px-3 py-1 rounded-full">
                        <span>{s}</span>
                        <button
                          type="button"
                          onClick={() => removeSkill(s)}
                          className="ml-2 text-gray-500 hover:text-black"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label htmlFor="experience" className="block text-sm font-medium text-black mb-2">Years of Experience</label>
                  <input
                    type="number"
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    min="0"
                    max="50"
                    className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Education Qualifications <span className="text-red-500">*</span>
                  </label>
                  <EducationSelector
                    selectedEducation={formData.education}
                    onChange={(education) => setFormData(prev => ({ ...prev, education }))}
                    maxEducation={5}
                    required={true}
                    placeholder="Type your education qualification and press Enter..."
                  />
                </div>
              </div>
            )}
            
            {/* Resume Tab - Candidate only */}
            {isCandidate && activeTab === 'resume' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-black mb-2">Resume</label>
                  <div className="border-2 border-dashed border-gray-300 p-6 rounded-md text-center cursor-pointer hover:border-gray-400 transition-colors"
                       onClick={() => resumeFileRef.current.click()}
                  >
                    <FileText className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="mt-2">
                      <p className="text-sm font-medium text-black">
                        {formData.resumeUrl ? 'Replace current resume' : 'Upload your resume'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">PDF, DOCX or TXT up to 5MB</p>
                    </div>
                    <input
                      type="file"
                      ref={resumeFileRef}
                      onChange={handleResumeUpload}
                      accept=".pdf,.docx,.doc,.txt"
                      className="hidden"
                    />
                  </div>
                  
                  {formData.resumeUrl && (
                    <div className="mt-3 flex items-center justify-between bg-gray-50 p-3 rounded-md">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 text-gray-500 mr-2" />
                        <span className="text-sm">{formData.resumeUrl}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, resumeUrl: '' }))}
                        className="text-gray-500 hover:text-black"
                      >
                        &times;
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Company Details Tab - Employer only */}
            {!isCandidate && activeTab === 'company' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="industry" className="block text-sm font-medium text-black mb-2">Industry</label>
                  <input
                    type="text"
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors"
                    placeholder="e.g. Technology, Healthcare, Finance"
                  />
                </div>
                
                <div>
                  <label htmlFor="companySize" className="block text-sm font-medium text-black mb-2">Company Size</label>
                  <select
                    id="companySize"
                    name="companySize"
                    value={formData.companySize}
                    onChange={handleChange}
                    className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors"
                  >
                    <option value="">Select company size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501-1000">501-1000 employees</option>
                    <option value="1001+">1001+ employees</option>
                  </select>
                </div>
              </div>
            )}
            
            <div className="flex justify-end border-t border-black pt-6 mt-6">
              <button
                type="submit"
                className="px-6 py-2 bg-black text-white font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || imageError ? true : false}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </div>
                ) : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default ProfileSettingsPage;
